<div class="section-div">
  <span class="sr-only" aria-live="polite" role="status">{{ loadingMessage }}</span>
  <p class="section-title">Plan Your Path</p>

  @if (isLoading) {
    <div class="loading-state" aria-hidden="true">
      <img src="/assets/animate/beaker-loader-dark.gif" alt="" class="loading-icon">
      <p>Loading your career plan...</p>
    </div>
  } @else if (isError) {
    <div class="error-state">
      <p>{{ isError }}</p>
    </div>
  } @else {
    <div class="user-div">
      <div class="user-card">
        <div class="card-header user-dropdown">
          <div class="header-row">
            <img src="/assets/icons/build-plan/user-profile.svg" alt="profile" />
            <p class="card-title">You</p>
          </div>
          <div (click)="toggleUserDropdown()">
            <img src="/assets/icons/build-plan/menu-option.svg" alt="menu" />
          </div>
          @if (isUserDropdownOpen) {
            <div class="dropdown-menu">
              <div 
                class="dropdown-item"
                routerLink="/profile"
                (click)="onEditProfile()"
              >
                Edit Profile
              </div>
            </div>
          }
        </div>
        <div class="horizontal-line"></div>
        <div class="card-content">
          <p class="content-header">{{ userData.firstName }} {{ userData.lastName }}</p>
          <div class="content-items">
            <div class="content-item-row">
              <img src="/assets/icons/build-plan/education-icon.svg" alt="education" />
              <p class="content-item-text">
                <strong>Education</strong> {{ userData.grade }}
              </p>
            </div>
            <div class="content-item-row">
              <img src="/assets/icons/build-plan/major-icon.svg" alt="major" />
              <p class="content-item-text">
                <strong>Major</strong> {{ userMajor?.label || 'N/A' }}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="match-line-div">
        <img src="/assets/icons/build-plan/match-line.svg" alt="match-score" />
      </div>
  
      <div class="user-card">
        <div class="card-header goal-dropdown">
          <div class="header-row">
            <img src="/assets/icons/build-plan/user-goal.svg" alt="profile" />
            <p class="card-title">Goal</p>
          </div>
          <div (click)="toggleGoalDropdown()">
            <img src="/assets/icons/build-plan/menu-option.svg" alt="menu" />
          </div>
          @if (isGoalDropdownOpen) {
            <div class="dropdown-menu">
              <div 
                class="dropdown-item"
                [routerLink]="`/career-details/${encodedCareerGoalOnetCode}`"
                (click)="onViewOccupationDetails()"
              >
                View Occupation Details
              </div>
              <div class="dropdown-divider"></div>
              <div 
                class="dropdown-item"
                routerLink="/profile"
                (click)="onChangeWorkLocation()"
              >
                Change Work Location
              </div>
              <div class="dropdown-divider"></div>
              <div 
                class="dropdown-item"
                routerLink="/career-matches"
                (click)="onUpdateCareerGoal()"
              >
                Update Career Goal
              </div>
            </div>
          }
        </div>
        <div class="horizontal-line"></div>
        <div class="card-content">
          <p class="content-header">{{ careerTitle }}</p>
          <div class="content-items">
            <div class="content-item-row">
              <img src="/assets/icons/build-plan/salary-icon.svg" alt="salary" />
              <p class="content-item-text">
                <strong>Median Salary in 
                  {{ userData.futureLocation }}
                </strong> {{ careerSalary }}
              </p>
            </div>
            <div class="content-item-row">
              <img src="/assets/icons/build-plan/growth-icon.svg" alt="growth" />
              <p class="content-item-text">
                <strong>{{ careerGrowthState }} Job Growth Rate</strong> {{ careerGrowth }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  
    <div class="skills-match-div">
      <div class="skills-match-header">
        <p class="skills-header">How your skills match up</p>
        <p class="skills-desc">
          These are suggestions to help you grow your strengths, 
          close skills to strengthen and move closer to your goal.
        </p>
      </div>
  
      <div class="skills-content">
        <div class="skills-box">
          <p class="skill-content-title">Aligned Skills</p>
          <div class="horizontal-line"></div>
          <div class="skills-data">
            @for (item of skills; track $index) {
              <div class="skill">
                <p class="skill-text">{{ item }}</p>
              </div>
            }
          </div>
        </div>
  
        <div class="skills-box">
          <p class="skill-content-title">Skills to Strengthen</p>
          <div class="horizontal-line"></div>
          <div class="skills-data">
            @for (item of skillGaps; track $index) {
              <div class="skill">
                <p class="skill-text">{{ item }}</p>
              </div>
            }
          </div>
        </div>
      </div>
  
      <div class="work-next-div">
        <div class="work-next-header">
          <p class="next-header-text">What to work on next</p>
          <p class="next-desc">
            {{ planPathData.summary }}
          </p>
        </div>
        <div class="next-items-div">
          <div class="next-item-box">
            <img src="/assets/icons/build-plan/circle-no-1.svg" alt="1" />
            <div class="next-item-data">
              <p class="next-item-title">Update your resume</p>
              <p class="next-item-desc">
                Your resume will change as you gain new skills, coursework, or experience. You can return at any time to upload a more recent version—doing so helps keep your Compass profile accurate and your recommendations personalized.
                <a class="refresh-resume-link" (click)="openResumeDialog()">Refresh resume</a>
              </p>
            </div>
          </div>
          @for (item of nextItemList; track $index) {
            <div class="next-item-box">
              <img [src]="`/assets/icons/build-plan/circle-no-${$index + (hasResume ? 2 : 1)}.svg`" alt="list" />
              <div class="next-item-data">
                <p class="next-item-title">{{ item.label }}</p>
                <p class="next-item-desc">
                  {{ planPathData.plan[item.key] }}
                </p>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>